import type { ComponentConfig } from '@puckeditor/core'
import type { ReactNode } from 'react'
import ColorField from './ColorField'
import { RichText, richTextField } from './RichText'
import PaddingField, { type PaddingValue, defaultPadding, paddingToStyle } from './PaddingField'

export interface HeadingProps {
    text: string | ReactNode
    level: 'h1' | 'h2' | 'h3'
    color: string
    alignment: 'left' | 'center' | 'right'
    fontWeight: string
    padding: PaddingValue
}

const levelSizes: Record<string, number> = {
    h1: 32,
    h2: 24,
    h3: 20,
}

export const HeadingBlock: ComponentConfig<HeadingProps> = {
    label: 'Heading',
    defaultProps: {
        text: 'Heading',
        level: 'h2',
        color: '#111827',
        alignment: 'left',
        fontWeight: '700',
        padding: defaultPadding,
    },
    fields: {
        text: richTextField('Text', false),
        level: {
            type: 'radio',
            label: 'Size',
            options: [
                { label: 'H1', value: 'h1' },
                { label: 'H2', value: 'h2' },
                { label: 'H3', value: 'h3' },
            ],
        },
        color: {
            type: 'custom',
            render: ({ value, onChange }) => <ColorField label="Color" value={value} onChange={onChange} />,
        },
        alignment: {
            type: 'radio',
            label: 'Alignment',
            options: [
                { label: 'Left', value: 'left' },
                { label: 'Center', value: 'center' },
                { label: 'Right', value: 'right' },
            ],
        },
        fontWeight: {
            type: 'select',
            label: 'Font Weight',
            options: [
                { label: 'Normal', value: '400' },
                { label: 'Medium', value: '500' },
                { label: 'Semi Bold', value: '600' },
                { label: 'Bold', value: '700' },
            ],
        },
        padding: {
            type: 'custom',
            render: ({ value, onChange }) => <PaddingField value={value} onChange={onChange} />,
        },
    },
    render: ({ text, level, color, alignment, fontWeight, padding }) => {
        const Tag = level === 'h1' || level === 'h3' ? level : 'h2'
        return (
            <RichText as={Tag} value={text} style={{
                color,
                textAlign: alignment,
                fontWeight,
                fontSize: levelSizes[Tag],
                margin: 0,
                padding: paddingToStyle(padding),
                lineHeight: 1.3,
            }} />
        )
    },
}
